import { routeApis } from "../../constants/apis";
import { RequestMethods } from "../../constants/fetch";
import { UNEXPECTED_ERROR } from "../../constants/messages";
import { Routes } from "../../constants/routes";
import { ClientPortalPageRestrictionPermissions } from "../../constants/user";
import { fetchJSON, fetchWithTimeOut, setFetchOpts } from "../../helpers/fetch";
import { UserPageRestriction } from "../../types/global";
import { getPageData } from "../templates/page";

/**
 * Fetches the list of available user statuses from the API.
 *
 * @param {string} url - The API endpoint URL to fetch user statuses from
 * @returns {Promise<any>} The user statuses data
 */
export const getUserStatuses = async (url: string) => {
  const fetchOptions = setFetchOpts({
    method: RequestMethods.GET,
  });

  const { data } = await fetchJSON(url, fetchOptions, 10000);

  return data;
};

/**
 * React Router loader for the portal user detail page.
 * Fetches a single portal user by their Azure User ID from the route parameters.
 *
 * @param params - Route parameters object
 * @param params.params.azureUserId - The Azure User ID from the route
 * @returns User data and error state, or throws a Response for error cases
 * @throws {Response} 500 error if API request fails
 * @throws {Response} 404 error if user is not found
 */
export const portalUserDetailLoader = async ({ params }: any) => {
  const { azureUserId } = params;

  // Fetch portal user with userId as query parameter
  const result = await getPageData({
    url: routeApis[Routes.PORTAL_USERS_AZUREUSERID].portalUser,
    params: { userId: azureUserId },
  });

  // If there was an error fetching data, throw a 500 error
  if (result.error) {
    throw new Response("", { status: 500, statusText: result.error });
  }

  const user = result.data;

  // If no user found, throw a 404 error
  if (!user) {
    throw new Response("", { status: 404, statusText: "User not found" });
  }

  return { data: user, azureUserId, error: null };
};

/**
 * Updates the page access restrictions for a portal user.
 * Takes the form values, maps them to page restrictions, and sends a PUT request to update them.
 *
 * @param params - Update parameters
 * @param params.formValues - Form values containing new restriction permissions
 * @param params.pageRestrictions - Array of current page restrictions to be updated
 * @param params.item - User item containing the azureUserId
 * @returns Object with error property (undefined if successful, error message if failed)
 */
export const updatePortalUser = async ({
  formValues,
  pageRestrictions,
  item,
}: {
  formValues: Record<string, ClientPortalPageRestrictionPermissions>;
  pageRestrictions: Array<UserPageRestriction>;
  item: any;
}) => {
  // Update page restrictions with new values from form
  pageRestrictions.forEach((pageRestriction: UserPageRestriction) => {
    const newRestrictionValue: ClientPortalPageRestrictionPermissions =
      formValues[`restriction:${pageRestriction.pageRestrictionId}`];
    if (newRestrictionValue)
      pageRestriction.restrictionPermission = newRestrictionValue;
  });

  const { azureUserId } = item;

  const body = {
    userId: azureUserId,
    pageRestrictions,
  };

  const fetchOptions = setFetchOpts({
    method: RequestMethods.PUT,
    body,
  });

  try {
    const response = await fetchWithTimeOut(
      routeApis[Routes.PORTAL_USERS].userPageRestrictions,
      fetchOptions,
      10000
    );

    if (!response.ok) {
      return { error: UNEXPECTED_ERROR };
    } else {
      return { error: undefined };
    }
  } catch (error) {
    return { error: UNEXPECTED_ERROR };
  }
};

/**
 * Fetches email change request notes for a specific portal user.
 * Retrieves both approved and rejected email change requests with their details.
 *
 * @param params - Parameters object
 * @param params.userId - The Azure User ID to fetch notes for
 * @returns Object containing either the notes data or an error message
 */
export const getPortalUserNotes = async ({ userId }: { userId: string }) => {
  const fetchOptions = setFetchOpts({
    method: RequestMethods.GET,
  });

  try {
    const response = await fetchJSON(
      `${
        routeApis[Routes.PENDING_EMAIL_CHANGES].getPendingItems
      }/Notes/${userId}`,
      fetchOptions,
      10000
    );

    const { error, data } = response;

    if (error) {
      return { error: UNEXPECTED_ERROR };
    } else {
      return { data };
    }
  } catch (error) {
    return { error: UNEXPECTED_ERROR };
  }
};

/**
 * Reactivates a client portal user account.
 * Used for users with Rejected or Removed status to restore their access.
 *
 * @param params - Parameters object
 * @param params.azureUserId - The Azure User ID of the user to reactivate
 * @returns Object containing either success data or an error message
 */
/**
 * Updates the folder permissions for a portal user.
 * Takes the form values (selected folder IDs), rebuilds the folder permissions JSON,
 * and sends a PUT request to update them in SharePoint.
 *
 * @param params - Update parameters
 * @param params.formValues - Form values containing the selected folder IDs
 * @param params.folderPermissionsList - Original list of folder permissions with names
 * @param params.folderPermissions - User's folder permissions metadata from SharePoint
 * @returns Object with error property (undefined if successful, error message if failed)
 */
export const updateUserFolderPermissions = async ({
  formValues,
  folderPermissionsList,
  folderPermissions,
}: {
  formValues: Record<string, any>;
  folderPermissionsList: Array<{ name: string; hasPermission: string }>;
  folderPermissions: {
    listItemUniqueId: string;
    salesForceContactId: string;
    userFolderPermissionsListName: string;
    folderPermissionsJson: string;
  };
}) => {
  // Helper to convert folder name to a valid hyphenated ID (same as in form generation)
  const toHyphenatedId = (name: string): string => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, "")
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-")
      .replace(/^-|-$/g, "");
  };

  // Get the selected folder IDs from form values
  const selectedFolderIds: Array<string> = formValues["allowed-folders"] || [];

  // Rebuild the folder permissions list with updated hasPermission values
  const updatedFolders = folderPermissionsList.map((folder) => ({
    name: folder.name,
    hasPermission: selectedFolderIds.includes(toHyphenatedId(folder.name))
      ? "true"
      : "false",
  }));

  // Create the updated JSON string
  const updatedFolderPermissionsJson = JSON.stringify({
    folders: updatedFolders,
  });

  const body = {
    ListItemUniqueId: folderPermissions.listItemUniqueId,
    SalesForceContactId: folderPermissions.salesForceContactId,
    UserFolderPermissionsListName:
      folderPermissions.userFolderPermissionsListName,
    FolderPermissionsJson: updatedFolderPermissionsJson,
  };

  const fetchOptions = setFetchOpts({
    method: RequestMethods.PUT,
    body,
  });

  try {
    const response = await fetchWithTimeOut(
      routeApis[Routes.PORTAL_USERS].userFolderPermissions,
      fetchOptions,
      10000
    );

    if (!response.ok) {
      return { error: UNEXPECTED_ERROR };
    } else {
      return { error: undefined };
    }
  } catch (error) {
    return { error: UNEXPECTED_ERROR };
  }
};

export const reactivatePortalUser = async ({
  azureUserId,
}: {
  azureUserId: string;
}) => {
  const fetchOptions = setFetchOpts({
    method: RequestMethods.POST,
    body: {
      azureUserId,
    },
  });

  try {
    const response = await fetchJSON(
      `${routeApis[Routes.PORTAL_USERS].portalUsers}/Reactivate`,
      fetchOptions,
      10000
    );

    const { error, data } = response;

    if (error) {
      return { error: UNEXPECTED_ERROR };
    } else {
      return { data };
    }
  } catch (error) {
    return { error: UNEXPECTED_ERROR };
  }
};
